import { useTranslation } from 'react-i18next';

import QuestionsTabContent from '@/components/activity/QuestionsTabContent';
import { ChallengeEditing } from '@/components/admin/lessons/ChallengeEditing';
import LessonFormService, {
  LessonFormServiceSuccess,
} from '@/components/lesson/LessonFormService';
import ResourcesTabContent from '@/components/resource/ResourcesTabContent';
import WorkspaceLayout from '@/components/shared/WorkspaceLayout';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton, Tabs } from '@/components/ui/daisy';
import { ORDENABLE_PAGE_SIZE } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import { VersioningProvider } from '@/data/context/VersioningContext';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useLessonReplacement from '@/data/hook/lesson/useLessonReplacement';
import useLessonWorkspace from '@/data/hook/lesson/useLessonWorkspace';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import {
  useInfiniteCache,
  useListCache,
} from '@/data/hook/query/v2/useCacheManager';
import useWorkspaceTabs from '@/data/hook/useWorkspaceTabs';
import { updateLessonIntoBlock } from '@/data/services/lessonBlocksServices';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import MaterialsTabContent from '@/components/material/MaterialsTabContent';

export enum LessonTabs {
  INFO = 'info',
  QUESTIONS = 'questions',
  RESOURCES = 'resources',
  MATERIALS = 'materials',
  CHALLENGE = 'challenge',
}

export default function LessonWorkspace() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonForm.workspace',
  });
  const { tree } = useTreeCourses();
  const { lesson, slug, courseLineId, moduleId, unitId, lessonId, isLoading } =
    useLessonWorkspace();
  const { coursePath } = usePedWorkspace();
  const subcategory = coursePath?.course?.module?.courseLine?.subcategory;
  const { tab, setTab } = useWorkspaceTabs<LessonTabs>({
    defaultTab: LessonTabs.INFO,
  });
  const { navigate } = useCourseTreeNavigate();
  const { replaceLesson } = useLessonReplacement();

  const cacheManager = useInfiniteCache(
    lessonBlocksQueryKeys.listLessons(slug ?? '', unitId)._ctx.infinity
      .queryKey,
  );

  const dndCacheManager = useListCache(
    lessonBlocksQueryKeys.listLessons(slug ?? '', unitId, {
      pageSize: ORDENABLE_PAGE_SIZE,
    }).queryKey,
  );

  const handleSuccessUpdate = ({ item }: LessonFormServiceSuccess) => {
    if (lessonId !== item.id) {
      replaceLesson(item.id);
      return;
    }

    tree?.current.update({
      id: mountKey({ type: 'lesson', id: item.id }),
      name: item.name,
    });
  };

  const handleSuccessCreate = (response: LessonFormServiceSuccess) => {
    const lessonKey = mountKey({ type: 'lesson', id: response.item.id });
    cacheManager.add(response.item);
    dndCacheManager.add(response.item);
    tree?.current.insert(
      {
        id: lessonKey,
        name: response.item.name,
        type: 'lesson',
        realId: response.item.id,
      },
      mountKey({ type: 'unit', id: unitId }),
    );
    navigate(
      ROUTES.ADMIN.COURSE_LINES.LESSON(
        courseLineId,
        moduleId,
        slug ?? '',
        unitId,
        response.item.id,
      ),
      { treeKey: lessonKey },
    );
  };
  const handleSuccess = (response: LessonFormServiceSuccess) => {
    if (response.action === 'update') handleSuccessUpdate(response);
    else handleSuccessCreate(response);
  };

  if (isLoading) return <LoadingView />;

  return (
    <WorkspaceLayout
      title={t('title')}
      subtitle={lesson?.name || t('newLesson')}
      description={lesson?.description}
    >
      <Tabs
        variant="box"
        className="w-fit"
        value={tab}
        onChange={value => setTab(value as LessonTabs)}
      >
        <Tabs.Tab value={LessonTabs.INFO}>{t('tabs.information')}</Tabs.Tab>
        <Tabs.Tab value={LessonTabs.QUESTIONS} disabled={!lesson}>
          {t('tabs.questions')}
        </Tabs.Tab>
        <Tabs.Tab value={LessonTabs.RESOURCES} disabled={!lesson}>
          {t('tabs.resources')}
        </Tabs.Tab>
        <Tabs.Tab value={LessonTabs.MATERIALS} disabled={!lesson}>
          {t('tabs.materials')}
        </Tabs.Tab>
        <Tabs.Tab value={LessonTabs.CHALLENGE} disabled={!lesson}>
          {t('tabs.challenge')}
        </Tabs.Tab>
      </Tabs>
      <ConditionalRenderer condition={tab === LessonTabs.INFO}>
        <LessonFormService key={lesson?.id} onSuccess={handleSuccess} />
      </ConditionalRenderer>
      {lesson && (
        <VersioningProvider
          key={lesson.id}
          model={lesson}
          createVersion={id => updateLessonIntoBlock(slug ?? '', unitId, id)}
          onVersioned={versioned => replaceLesson(versioned.id)}
        >
          <ConditionalRenderer condition={tab === LessonTabs.QUESTIONS}>
            <QuestionsTabContent
              lessonId={lesson.id}
              subcategory={subcategory}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={tab === LessonTabs.RESOURCES}>
            <ResourcesTabContent lessonId={lesson.id} />
          </ConditionalRenderer>
          <ConditionalRenderer condition={tab === LessonTabs.CHALLENGE}>
            <ChallengeEditing
              lesson={lesson}
              slug={slug}
              blockId={unitId}
              onUpdate={updated => replaceLesson(updated.id)}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={tab === LessonTabs.MATERIALS}>
            <MaterialsTabContent
              lesson={lesson}
              unitId={unitId}
              onLessonReplaced={replaceLesson}
            />
          </ConditionalRenderer>
        </VersioningProvider>
      )}
    </WorkspaceLayout>
  );
}

const LoadingView = () => {
  return (
    <div className="flex flex-col flex-1 rounded-2xl p-6 gap-6 bg-base-100">
      <div className="flex flex-col gap-5">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-8 w-40" />
      </div>
      <Skeleton className="h-12 w-96" />
      <Skeleton className="h-96 w-full" />
      <div className="flex justify-end">
        <Skeleton className="h-12 w-32" />
      </div>
    </div>
  );
};
